/**
 * Generic Klaviyo Back-in-Stock handler.
 * Given a container element with standard `data-js` hooks, handles form submission
 * to Klaviyo's BIS endpoint and optionally subscribes to a mailing list.
 */
(function () {
  if (window.KlaviyoNotifyMe) return;

  const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const KLAVIYO_LIST_SUBSCRIBE_ENDPOINT = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';
  const KLAVIYO_API_REVISION = '2024-10-15';
  const NOTIFY_SUCCESS_DISMISS_MS = 2400;

  class KlaviyoNotifyMe {
    /**
     * @param {HTMLElement} panel - The main `data-js="notify-me"` container
     * @param {Object} [options]
     * @param {Function} [options.onResize] - Called when the form state changes (e.g. error shown)
     * @param {Function} [options.onSuccess] - Called after a successful submission, before auto-close
     * @param {Function} [options.onClose] - Called to trigger the panel's close animation
     */
    constructor(panel, options = {}) {
      this.panel = panel;
      this.options = options;

      this.form = panel.querySelector('[data-js="notify-me-form"]');
      this.variantInput = panel.querySelector('[data-js="notify-me-variant-id"]');
      this.emailInput = panel.querySelector('[data-js="notify-me-email"]');
      this.marketingCheck = panel.querySelector('[data-js="notify-me-marketing"]');
      this.errorEl = panel.querySelector('[data-js="notify-me-error"]');
      this.successEl = panel.querySelector('[data-js="notify-me-success"]');
      this.submitBtn = panel.querySelector('[data-js="notify-me-submit"]');
      this.submitLabel = panel.querySelector('[data-js="notify-me-submit-label"]');
      this.submitSpinner = panel.querySelector('[data-js="notify-me-submit-spinner"]');

      this.successTimer = null;

      this.handleSubmit = this.handleSubmit.bind(this);
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }
    }

    /**
     * Reset the form to its initial state.
     */
    reset() {
      if (this.successTimer) {
        clearTimeout(this.successTimer);
        this.successTimer = null;
      }
      if (this.form) {
        this.form.hidden = false;
        delete this.form.dataset.state;
      }
      if (this.emailInput) {
        this.emailInput.value = '';
        this.emailInput.disabled = false;
      }
      if (this.marketingCheck) {
        this.marketingCheck.checked = false;
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      if (this.successEl) {
        this.successEl.hidden = true;
        delete this.successEl.dataset.state;
      }
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
      }
      if (this.submitLabel) {
        this.submitLabel.hidden = false;
      }
      if (this.submitSpinner) {
        this.submitSpinner.hidden = true;
      }
    }

    async handleSubmit(e) {
      e.preventDefault();
      if (!this.emailInput || !this.variantInput) return;

      const email = (this.emailInput.value || '').trim();
      const variantId = String(this.variantInput.value || '').trim();
      const companyId = this.panel.dataset.klaviyoId;
      const listId = this.panel.dataset.klaviyoList || '';

      if (!email || !variantId || !companyId) {
        this.showError('missing_fields');
        return;
      }

      this.submitBtn.disabled = true;
      this.emailInput.disabled = true;
      if (this.submitLabel) this.submitLabel.hidden = true;
      if (this.submitSpinner) this.submitSpinner.hidden = false;
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        if (this.options.onResize) this.options.onResize();
      }

      const bisPayload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + variantId,
              },
            },
          },
        },
      };

      try {
        const res = await fetch(KLAVIYO_BIS_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_API_REVISION,
          },
          body: JSON.stringify(bisPayload),
        });

        if (res.status !== 202) {
          throw new Error('klaviyo_bis_failed_' + res.status);
        }

        if (this.marketingCheck && this.marketingCheck.checked && listId) {
          // Fire-and-forget list subscription
          this.subscribeToList(listId, email).catch(function () {});
        }

        this.showSuccess();
      } catch (_err) {
        this.showError('submit_failed');
        this.submitBtn.disabled = false;
        this.emailInput.disabled = false;
        if (this.submitLabel) this.submitLabel.hidden = false;
        if (this.submitSpinner) this.submitSpinner.hidden = true;
      }
    }

    showSuccess() {
      if (this.successEl) {
        this.successEl.hidden = false;
        requestAnimationFrame(() => {
          this.successEl.dataset.state = 'visible';
        });
      }
      if (this.form) this.form.dataset.state = 'hidden';

      if (this.options.onSuccess) {
        this.options.onSuccess();
      }

      setTimeout(() => {
        if (this.form) this.form.hidden = true;
        if (this.options.onResize) this.options.onResize();
      }, 320);

      this.successTimer = setTimeout(() => {
        this.successTimer = null;
        if (this.options.onClose) this.options.onClose();
      }, NOTIFY_SUCCESS_DISMISS_MS);
    }

    showError(key) {
      if (!this.errorEl) return;
      const messages = {
        missing_fields: this.errorEl.dataset.errorInvalidEmail || 'Please enter a valid email address.',
        submit_failed: this.errorEl.dataset.errorGeneric || 'Something went wrong. Please try again.',
      };
      this.errorEl.textContent = messages[key] || messages.submit_failed;
      this.errorEl.hidden = false;
      if (this.options.onResize) this.options.onResize();
    }

    async subscribeToList(listId, email) {
      const body = new URLSearchParams();
      body.set('g', listId);
      body.set('email', email);
      await fetch(KLAVIYO_LIST_SUBSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Cache-Control': 'no-cache',
        },
        body: body.toString(),
      });
    }
  }

  window.KlaviyoNotifyMe = KlaviyoNotifyMe;
})();
